<template>
    <div class="sc__analysis__tab__box tab-content">
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <div class="head__title__group">
                    <h3 class="title">Preferences List</h3>
                </div>
                <div class="print__data__btn">
                    <WorkpacketPdf :button-label="`Download`" :assessmentId="assessmentId"
                        :organizationId="organizationId" :learnerId="learnerId"
                        v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>

            <div class="head__label__box__wrapper main__head__info__wrapper">
                <div class="head__information__wrapper">
                    <p>The preferences list can inform potential speech targets as vocal - verbal mands</p>
                    <p>Info sources: Preference tests, informal observations of high - preference items.</p>
                </div>
                <div class="head__information__wrapper">
                    <h6 class="info__title">Directions</h6>
                    <ul>
                        <li>List potential reinforcers in the boxes below.</li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="preferences__list__options__box">
            <div class="options__box__wrapper">
                <label>Activities (Optional)</label>
                <div class="options__textarea__box">
                    <textarea v-model="preferences_list.activities" class="preferencesListNote"
                        placeholder="Type Here..."></textarea>
                </div>
            </div>
            <div class="options__box__wrapper">
                <label>Items (Optional)</label>
                <div class="options__textarea__box">
                    <textarea v-model="preferences_list.items" class="preferencesListNote"
                        placeholder="Type Here..."></textarea>
                </div>
            </div>
            <div class="options__box__wrapper">
                <label>People, Places (Optional)</label>
                <div class="options__textarea__box">
                    <textarea v-model="preferences_list.places" class="preferencesListNote"
                        placeholder="Type Here..."></textarea>
                </div>
            </div>
            <div class="section__btn__block">
                <button class="purple__save__btn" id="preferencesListBtn" @click="saveNote">Save</button>
            </div>
        </div>
        <div class="notice__box__wrapper">
            <div class="inner__note__cover__box">
                <div class="section__title__box">
                    <h6 class="title">Note:</h6>
                    <div class="print__btn disable__btn">
                        <a href="javascript:void(0)">
                            <span class="btn__icon">
                                <img :src="darkPrinterIcon">
                            </span>
                            <div class="btn__text">
                                <label>Print Data</label>
                                <span>(Coming soon)</span>
                            </div>
                        </a>
                    </div>

                </div>
                <div class="note__desc__wrapper">
                    <p>This list of reinforcers promotes training vocal - verbal targets as mands or mand -
                        tact's.</p>
                    <p>This list should be expanded to include words or phrases that are important to learn,
                        but that may not have particular reinforcing value at first. Example: items used
                        daily (fork, shop, shoes).</p>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted, inject } from 'vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import { showDialog } from '@/utils/swal';
import WorkpacketPdf from './WorkpacketPdf.vue';
import darkPrinterIcon from '@/assets/images/dark-printer.png';
import { authrizedPermissions } from '@/utils/permissions';

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    learnerId: Number,
    canConductAssessment: Boolean,
});

const loader = useLoaderStore();
const preferences_list = ref({
    activities: "",
    items: "",
    places: ""
});
const originalData = ref(null);
const assessment = ref(null);

// Register with parent for unsaved changes tracking
const registerUnsavedCheck = inject('registerUnsavedCheck', null);
const hasUnsavedChanges = () => {
    if (!originalData.value) return false;
    return JSON.stringify(preferences_list.value) !== JSON.stringify(originalData.value);
};
if (registerUnsavedCheck) {
    registerUnsavedCheck(hasUnsavedChanges);
}

const fetchAssessment = async () => {
    loader.show();
    try {
        const { data } = await api.get(`/organizations/${props.organizationId}/assessments/${props.assessmentId}`);
        assessment.value = data?.data || null
        const note = data?.data?.note?.preferences_list;

        preferences_list.value = (note) ? JSON.parse(note) : {
            activities: "",
            items: "",
            places: ""
        }
        // Store original data for unsaved changes detection
        originalData.value = JSON.parse(JSON.stringify(preferences_list.value));
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const saveNote = async () => {
    if (!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = assessment.value?.learner;
        if (learner?.is_administrator) {
            message = `You don’t have permission perform this action because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    loader.show();
    try {
        await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/notes`, {
            preferences_list: JSON.stringify(preferences_list.value)
        });

        // Update original data after successful save
        originalData.value = JSON.parse(JSON.stringify(preferences_list.value));

        showDialog({
            title: 'Success',
            icon: 'success',
            html: 'Note saved successfully!'
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

onMounted(fetchAssessment);
</script>